/**
 * WeeklyAssignmentsTab Component
 *
 * Displays weekly assignments grouped by auditor.
 * Data is loaded from CS Audit records in Gobodo (not localStorage).
 * Assignments are tracked via Auditor__c field on CS Audit records.
 */

import React, { useMemo, useCallback } from 'react';
import { ClipboardList, Users, Loader2 } from 'lucide-react';
import { useUsers, User } from '../../services/queries/userQueries';
import {
  useCSAudits,
  useUpdateCSAudit,
  useAccountIdToName,
  useAuditorIdToName,
  lookupAccountDisplayName,
  lookupAuditorDisplayName,
  useMeetingAgenda,
} from '../../services/queries/cyberSweepQueries';
import AuditorAssignmentCard, { WeeklyAssignment } from './AuditorAssignmentCard';
import { SelectedAuditData } from './AuditPickerModal';
import { filterPresentUsers } from '../../utils/cyberSweepUserFilters';

interface WeeklyAssignmentsTabProps {
  selectedWeek: string; // Format: YYYY-Www (e.g., "2024-W15")
}

// Get Monday date from week string (YYYY-Www format)
const getWeekStartDate = (weekString: string): string => {
  const [year, week] = weekString.split('-W').map(Number);
  const jan1 = new Date(year, 0, 1);
  const daysToMonday = (8 - jan1.getDay()) % 7;
  const firstMonday = new Date(jan1);
  firstMonday.setDate(jan1.getDate() + daysToMonday);
  const targetMonday = new Date(firstMonday);
  targetMonday.setDate(firstMonday.getDate() + (week - 1) * 7);
  return targetMonday.toISOString().split('T')[0];
};

// Helper to extract auditor reference from audit record
const getAuditorRef = (audit: Record<string, unknown>): string => {
  const data = (audit.data as Record<string, unknown>) ?? audit;
  return String(
    data.Auditor__c ?? data.auditor ?? data.custom__auditor ??
    audit.Auditor__c ?? audit.auditor ?? audit.custom__auditor ?? ''
  );
};

// Helper to extract assignment status from audit record
const getAssignmentStatus = (audit: Record<string, unknown>): 'not_started' | 'in_progress' | 'completed' => {
  const data = (audit.data as Record<string, unknown>) ?? audit;
  const status = String(
    data.Assignment_Status__c ?? data.assignmentStatus ?? data.custom__assignment_status ?? ''
  ).toLowerCase();

  if (status === 'completed' || status === 'complete') return 'completed';
  if (status === 'in_progress' || status === 'in progress' || status === 'working') return 'in_progress';
  return 'not_started';
};

// Helper to extract remarks from audit record
const getAssignmentRemarks = (audit: Record<string, unknown>): string => {
  const data = (audit.data as Record<string, unknown>) ?? audit;
  return String(
    data.Assignment_Remarks__c ?? data.assignmentRemarks ?? data.custom__assignment_remarks ?? ''
  );
};

const WeeklyAssignmentsTab: React.FC<WeeklyAssignmentsTabProps> = ({ selectedWeek }) => {
  const { data: allUsers = [] } = useUsers({});
  // Fetch ALL CS Audits for assignment display
  const { data: csAudits = [], isLoading: auditsLoading } = useCSAudits(undefined, { fetchAllCSAudits: true });
  const updateCSAudit = useUpdateCSAudit();

  // Resolve names
  const { data: accountNameMap } = useAccountIdToName(csAudits as Record<string, unknown>[]);
  const { data: auditorNameMap } = useAuditorIdToName(csAudits as Record<string, unknown>[]);

  const weekStartDate = getWeekStartDate(selectedWeek);

  // Get attendance data from meeting agenda to filter to present users
  const { data: agendaRecord, isLoading: agendaLoading } = useMeetingAgenda(weekStartDate);

  // Check if we have attendance data with at least one present user
  const hasAttendanceData = useMemo(() => {
    const attendance = agendaRecord?.attendance || [];
    return attendance.some(a => a.status === 'present');
  }, [agendaRecord?.attendance]);

  // Filter to users who are marked "present" in attendance
  // Returns empty array if no attendance data - forces user to mark attendance first
  const cyberSweepUsers = useMemo(() => {
    const attendance = agendaRecord?.attendance || [];
    if (attendance.length > 0) {
      // Filter to only users marked as present
      return filterPresentUsers(allUsers, attendance);
    }
    // Return empty if no attendance recorded - user must mark attendance first
    return [];
  }, [allUsers, agendaRecord?.attendance]);

  // Convert CS Audit records to WeeklyAssignment format
  const convertAuditToAssignment = useCallback((
    audit: Record<string, unknown>,
    auditorId: string,
    auditorName: string,
    index: number
  ): WeeklyAssignment => {
    const data = (audit.data as Record<string, unknown>) ?? audit;
    const auditId = String(audit.id ?? audit.key ?? '');
    const auditName = String(audit.Name__c ?? audit.name ?? data.Name__c ?? data.name ?? audit.label ?? '');
    const clientRef = String(
      audit.Account__c ?? audit.custom__account ?? audit.ClientId__c ??
      data.Account__c ?? data.custom__account ?? data.ClientId__c ?? ''
    );
    const clientName = lookupAccountDisplayName(accountNameMap, clientRef) || clientRef;
    const auditStatus = String(
      audit.auditTypeDisplayName ?? data.Phase_Status__c ?? data.phaseStatus ??
      data.Audit_Type__c ?? data.auditType ?? ''
    );
    const dueDate = String(data.Due_Date__c ?? data.dueDate ?? data.Due_Date ?? '');

    return {
      id: auditId,
      weekStartDate,
      auditorId,
      auditorName,
      clientName,
      clientId: clientRef,
      auditId,
      auditName,
      auditStatus,
      dueDate,
      status: getAssignmentStatus(audit),
      remarks: getAssignmentRemarks(audit),
      sortOrder: index,
    };
  }, [accountNameMap, weekStartDate]);

  // Get assignments for a specific auditor (audits where Auditor__c matches)
  const getAuditorAssignments = useCallback((auditorId: string, auditorName: string): WeeklyAssignment[] => {
    const assignments: WeeklyAssignment[] = [];
    let index = 0;

    csAudits.forEach((audit: Record<string, unknown>) => {
      const auditorRef = getAuditorRef(audit);
      if (!auditorRef) return;

      // Check if this audit is assigned to this auditor
      // Match by ID or by resolved name
      const resolvedName = lookupAuditorDisplayName(auditorNameMap, auditorRef);
      const isAssignedToAuditor =
        auditorRef === auditorId ||
        auditorRef.toLowerCase() === auditorId.toLowerCase() ||
        resolvedName.toLowerCase() === auditorName.toLowerCase();

      if (isAssignedToAuditor) {
        assignments.push(convertAuditToAssignment(audit, auditorId, auditorName, index));
        index++;
      }
    });
    return assignments.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [csAudits, auditorNameMap, convertAuditToAssignment]);

  // Handle assigning an audit to an auditor (update Auditor__c in Gobodo)
  const handleAddAssignment = useCallback(async (auditorId: string, auditData?: SelectedAuditData) => {
    if (!auditData?.auditId) {
      console.warn('[WeeklyAssignmentsTab] No audit selected');
      return;
    }

    console.log('[WeeklyAssignmentsTab] Assigning audit to auditor:', {
      auditId: auditData.auditId,
      auditorId,
    });

    try {
      await updateCSAudit.mutateAsync({
        id: auditData.auditId,
        updates: {
          Auditor__c: auditorId,
          Assignment_Status__c: 'not_started',
          Assignment_Remarks__c: '',
        },
      });
      console.log('[WeeklyAssignmentsTab] Successfully assigned audit');
    } catch (error) {
      console.error('[WeeklyAssignmentsTab] Failed to assign audit:', error);
    }
  }, [updateCSAudit]);

  // Handle updating assignment status/remarks (update CS Audit record)
  const handleUpdateAssignment = useCallback(async (assignment: WeeklyAssignment) => {
    if (!assignment.auditId) return;

    console.log('[WeeklyAssignmentsTab] Updating assignment:', {
      auditId: assignment.auditId,
      status: assignment.status,
      remarks: assignment.remarks,
    });

    try {
      await updateCSAudit.mutateAsync({
        id: assignment.auditId,
        updates: {
          Assignment_Status__c: assignment.status,
          Assignment_Remarks__c: assignment.remarks || '',
        },
      });
    } catch (error) {
      console.error('[WeeklyAssignmentsTab] Failed to update assignment:', error);
    }
  }, [updateCSAudit]);

  // Handle unassigning an audit (clear Auditor__c)
  const handleDeleteAssignment = useCallback(async (auditId: string) => {
    console.log('[WeeklyAssignmentsTab] Unassigning audit:', auditId);

    try {
      await updateCSAudit.mutateAsync({
        id: auditId,
        updates: {
          Auditor__c: '',
          Assignment_Status__c: '',
          Assignment_Remarks__c: '',
        },
      });
      console.log('[WeeklyAssignmentsTab] Successfully unassigned audit');
    } catch (error) {
      console.error('[WeeklyAssignmentsTab] Failed to unassign audit:', error);
    }
  }, [updateCSAudit]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    let total = 0;
    let completed = 0;
    let inProgress = 0;

    cyberSweepUsers.forEach(user => {
      const assignments = getAuditorAssignments(user.id, `${user.firstName} ${user.lastName}`);
      total += assignments.length;
      completed += assignments.filter(a => a.status === 'completed').length;
      inProgress += assignments.filter(a => a.status === 'in_progress').length;
    });

    return { total, completed, inProgress };
  }, [cyberSweepUsers, getAuditorAssignments]);

  // Loading state
  if (auditsLoading || agendaLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading assignments...</span>
        </div>
      </div>
    );
  }

  // Empty state - no attendance recorded yet
  if (!hasAttendanceData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Users className="h-10 w-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            No Attendance Recorded
          </h3>
          <p className="text-sm">
            Please mark attendance in the <strong>Meeting Agenda</strong> tab first.
          </p>
          <p className="text-sm mt-1 text-gray-400 dark:text-gray-500">
            Only team members marked as "present" will appear here for assignment.
          </p>
        </div>
      </div>
    );
  }

  // Empty state - no users marked present
  if (cyberSweepUsers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Users className="h-10 w-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            No Team Members Present
          </h3>
          <p className="text-sm">
            No team members are marked as "present" in attendance.
          </p>
          <p className="text-sm mt-1 text-gray-400 dark:text-gray-500">
            Go to the <strong>Meeting Agenda</strong> tab to update attendance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                Week of {new Date(weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {cyberSweepUsers.length} team member{cyberSweepUsers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-600 dark:text-gray-400">{summaryStats.completed} completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              <span className="text-gray-600 dark:text-gray-400">{summaryStats.inProgress} in progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              <span className="text-gray-600 dark:text-gray-400">{summaryStats.total - summaryStats.completed - summaryStats.inProgress} not started</span>
            </div>
          </div>
        </div>
      </div>

      {/* Auditor Cards */}
      <div className="space-y-3">
        {cyberSweepUsers.map((user, index) => {
          const auditorName = `${user.firstName} ${user.lastName}`;
          return (
            <AuditorAssignmentCard
              key={user.id}
              auditorId={user.id}
              auditorName={auditorName}
              assignments={getAuditorAssignments(user.id, auditorName)}
              weekStartDate={weekStartDate}
              onAddAssignment={handleAddAssignment}
              onUpdateAssignment={handleUpdateAssignment}
              onDeleteAssignment={handleDeleteAssignment}
              defaultExpanded={index === 0}
            />
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyAssignmentsTab;
